import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import FlashOnRoundedIcon from '@mui/icons-material/FlashOnRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import AutoGraphRoundedIcon from '@mui/icons-material/AutoGraphRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import Groups2RoundedIcon from '@mui/icons-material/Groups2Rounded';
import NorthEastRoundedIcon from '@mui/icons-material/NorthEastRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { getMeCached } from '../authCache';
import { fetchClasses } from '../stargarden/api/starGardenApi';
import {
  fetchMiniTaskClassStudents,
  fetchMiniTaskClassStatistics,
  fetchMiniTaskClassSummary,
} from '../api/miniTasks';
import { danduFontFamilies, teacherTypography } from '../../design/typography';

// MUI X Charts renders SVG labels outside MUI Typography. Keep chart text scoped
// to this report so its axes use the same readable Inter faces as our cards.
const chartTypographySx = {
  '&& .MuiChartsAxis-tickLabel, && .MuiChartsAxis-label, && .MuiChartsLegend-label': {
    fontFamily: danduFontFamilies.teacher,
    fontSize: 12,
    fontWeight: 500,
    fill: '#45556D',
  },
};
const descriptionSx = { ...teacherTypography.supportingText, fontSize: 14 };

const PERIOD_OPTIONS = [
  { value: 7, label: 'Son 7 gün' },
  { value: 14, label: 'Son 14 gün' },
  { value: 28, label: 'Son 28 gün' },
  { value: 56, label: 'Son 56 gün' },
];

const TASK_TYPE_LABELS = {
  turkish_l1_tour: 'Türkçe Yolculuğu',
  target_sound_hunt: 'N Sesini Bul',
  sound_discovery: 'N Sesini Keşfet',
  sound_hunt: 'A Sesini Yakala',
  letter_hunt: 'A Harflerini Yakala',
  multiple_choice: 'Ders Görevi',
  memory_matching: 'Hafıza',
  reflex_catch: 'Refleks',
  digit_recall: 'Sayı Hatırlama',
  odd_one_out: 'Farklı Olanı Bul',
  cargo_sorting: 'Kargo Ayrıştırma',
  pattern_recognition: 'Deseni Yakala',
};

const TASK_TYPE_SHORT_LABELS = {
  turkish_l1_tour: 'Türkçe Yolculuğu',
  target_sound_hunt: 'Ses Bulma',
  sound_discovery: 'Ses Keşfi',
  sound_hunt: 'Ses Avı',
  letter_hunt: 'Harf Avı',
  multiple_choice: 'Ders',
  memory_matching: 'Hafıza',
  reflex_catch: 'Refleks',
  digit_recall: 'Sayı Hatırlama',
  odd_one_out: 'Farklı',
  cargo_sorting: 'Kargo',
  pattern_recognition: 'Desen',
};

const LESSON_LABELS = {
  DKAB: 'Din Kültürü ve Ahlak Bilgisi',
  math: 'Matematik',
  TURKCE: 'Türkçe',
  ZIHIN: 'Zihin',
  GENEL_KULTUR: 'Genel Kültür',
  ENGLISH: 'English',
};

const SKILL_LABELS = {
  target_sound_hunt_n: 'N Sesini Bul',
  sound_discovery_n: 'N Sesini Keşfet',
  sound_hunt_a: 'Türkçe Yolculuğu',
  ...Object.fromEntries([1, 2, 4, 5].map((level) => [`turkish_l${level}_reinforcement`, `Türkçe Yolculuğu · L${level}`])),
  turkish_l1_l2_review: 'Türkçe Yolculuğu · Karma Tekrar',
  turkish_history_review: 'Türkçe Yolculuğu · Geçmişten Karma',
  letter_hunt_a: 'A Harflerini Yakala',
  ...Object.fromEntries([4, 5, 6, 7, 8].map((grade) => [`basic_religious_knowledge_${grade}`, `${grade}. Sınıf Temel Dinî Bilgiler`])),
  addition: 'Toplama',
  subtraction: 'Çıkarma',
  multiplication: 'Çarpma',
  division: 'Bölme',
  mixed_arithmetic: 'Karışık İşlemler',
  word_meaning_relations: 'Eş Anlam, Zıt Anlam, Eşses',
  punctuation_marks: 'Noktalama İşaretleri',
  bilgi_avi: 'Bilgi-Avı',
  dunyayi_tani: 'Dünyayı Tanı',
  turkiyeyi_tani: 'Ülkemizi Tanıyalım',
  memory_matching: 'Hafıza',
  reflex_catch: 'Refleks',
  digit_recall: 'Sayı Hatırlama',
  odd_one_out: 'Farklı Olanı Bul',
  cargo_sorting: 'Kargo Ayrıştırma',
  pattern_recognition: 'Deseni Yakala',
  vocabulary: 'Vocabulary',
};

function MetricCard({ icon, title, value, subtitle, tone = 'default' }) {
  const tones = {
    default: { borderColor: 'divider', background: '#ffffff', color: 'text.primary' },
    warm: { borderColor: '#f7c38b', background: 'linear-gradient(180deg, #fff9f1 0%, #fff4e2 100%)', color: '#7b3f00' },
    cool: { borderColor: '#93c5fd', background: 'linear-gradient(180deg, #f5faff 0%, #eaf4ff 100%)', color: '#0f3c78' },
    success: { borderColor: '#86efac', background: 'linear-gradient(180deg, #f3fff6 0%, #e9fbef 100%)', color: '#0f5132' },
  };
  const palette = tones[tone] || tones.default;

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: palette.borderColor,
        borderRadius: 3,
        p: { xs: 1.5, sm: 2 },
        background: palette.background,
        minWidth: 0,
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ color: palette.color, lineHeight: 0 }}>{icon}</Box>
          <Typography variant="body2" sx={teacherTypography.metricLabel}>
            {title}
          </Typography>
        </Stack>
        <Typography variant="h4" sx={{ ...teacherTypography.kpiValue, fontSize: { xs: 27, sm: 33 }, color: '#17213C' }}>
          {value}
        </Typography>
        <Typography variant="body2" sx={descriptionSx}>
          {subtitle}
        </Typography>
      </Stack>
    </Paper>
  );
}

function SectionCard({ title, subtitle, action, children, sx }) {
  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3.5,
        p: { xs: 1.75, sm: 2.25 },
        bgcolor: '#ffffff',
        minWidth: 0,
        ...sx,
      }}
    >
      <Stack spacing={1.5}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.25}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6" sx={teacherTypography.sectionTitle}>{title}</Typography>
            {subtitle ? (
              <Typography sx={{ ...descriptionSx, mt: 0.5, fontSize: { xs: '0.95rem', sm: '1rem' } }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          {action || null}
        </Stack>
        {children}
      </Stack>
    </Paper>
  );
}

function CoachNote({ title, items, tone = 'default', emptyText }) {
  const toneStyles = {
    default: { borderColor: '#dbeafe', background: '#f8fbff', chipColor: 'info' },
    success: { borderColor: '#bbf7d0', background: '#f4fff7', chipColor: 'success' },
    warning: { borderColor: '#fed7aa', background: '#fff8f1', chipColor: 'warning' },
  };
  const style = toneStyles[tone] || toneStyles.default;

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: style.borderColor,
        borderRadius: 3,
        p: 1.5,
        bgcolor: style.background,
      }}
    >
      <Stack spacing={1}>
        <Chip size="small" color={style.chipColor} label={title} sx={{ width: 'fit-content', fontWeight: 600 }} />
        {(items || []).length ? (
          (items || []).map((item) => (
            <Stack key={item} direction="row" spacing={1} alignItems="flex-start">
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'currentColor', color: 'text.primary', mt: '8px', flexShrink: 0 }} />
              <Typography variant="body2" sx={{ ...descriptionSx, lineHeight: 1.5 }}>
                {item}
              </Typography>
            </Stack>
          ))
        ) : (
          <Typography variant="body2" sx={descriptionSx}>
            {emptyText}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

function StudentSignalCard({ title, subtitle, items, emptyTitle, emptySeverity = 'info', tone = 'default' }) {
  const toneMap = {
    default: { borderColor: 'divider', background: '#fcfdff' },
    warning: { borderColor: '#fde68a', background: '#fffef6' },
  };
  const palette = toneMap[tone] || toneMap.default;

  return (
    <SectionCard title={title} subtitle={subtitle}>
      {(items || []).length ? (
        <Stack spacing={1.1}>
          {(items || []).map((item) => (
            <Paper
              key={item.student.id}
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: palette.borderColor,
                borderRadius: 3,
                p: 1.5,
                bgcolor: palette.background,
              }}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                <Box>
                  <Typography sx={teacherTypography.studentName}>{item.student.display_name}</Typography>
                  <Typography variant="body2" sx={descriptionSx}>
                    Son çözüm: {formatDateTime(item.last_round_at)}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
                  {typeof item.round_count !== 'undefined' ? (
                    <Chip label={`${item.round_count || 0} tur`} sx={{ fontWeight: 700 }} />
                  ) : null}
                  {item.avg_score_pct !== null && typeof item.avg_score_pct !== 'undefined' ? (
                    <Chip label={`Ort. ${formatPct(item.avg_score_pct)}`} color="success" variant="outlined" sx={{ fontWeight: 700 }} />
                  ) : null}
                  {typeof item.total_round_count !== 'undefined' ? (
                    <Chip
                      icon={<WarningAmberRoundedIcon />}
                      label={item.total_round_count ? `Toplam ${item.total_round_count} tur` : 'Henüz hiç çözmedi'}
                      sx={{ fontWeight: 700 }}
                    />
                  ) : null}
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      ) : (
        <Alert severity={emptySeverity}>{emptyTitle}</Alert>
      )}
    </SectionCard>
  );
}

function ActionCard({ index, title, children, tone = 'default' }) {
  const tones = {
    default: { borderColor: 'divider', background: '#ffffff' },
    warm: { borderColor: '#fed7aa', background: '#fff8f1' },
    cool: { borderColor: '#bfdbfe', background: '#f6fbff' },
    success: { borderColor: '#bbf7d0', background: '#f5fff7' },
  };
  const palette = tones[tone] || tones.default;

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: palette.borderColor,
        bgcolor: palette.background,
        borderRadius: 3,
        p: 1.75,
        height: '100%',
      }}
    >
      <Stack spacing={1}>
        <Chip label={`Adım ${index}`} size="small" sx={{ width: 'fit-content', fontWeight: 600 }} />
        <Typography sx={teacherTypography.cardTitle}>{title}</Typography>
        <Typography variant="body2" sx={descriptionSx}>
          {children}
        </Typography>
      </Stack>
    </Paper>
  );
}

const humanizeKey = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '-';
  return raw
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const lessonLabel = (lesson) => {
  if (!lesson) return '-';
  return LESSON_LABELS[lesson.code] || lesson.name || humanizeKey(lesson.code);
};

const skillLabel = (skillKey) => {
  return SKILL_LABELS[skillKey] || humanizeKey(skillKey);
};

const formatPct = (value) => {
  if (value === null || typeof value === 'undefined' || Number.isNaN(Number(value))) return '-';
  return `%${Number(value).toFixed(Number(value) % 1 ? 1 : 0)}`;
};

const formatLevel = (value) => {
  if (value === null || typeof value === 'undefined' || Number.isNaN(Number(value))) return '-';
  return Number(value).toFixed(Number(value) % 1 ? 1 : 0);
};

const formatDateShort = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '-';
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return new Intl.DateTimeFormat('tr-TR', { day: '2-digit', month: 'short' }).format(parsed);
};

const formatDateTime = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return 'Henüz hiç çözmedi';
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
};

const averageLevelFromDistribution = (distribution) => {
  const entries = Object.entries(distribution || {});
  const totalStudents = entries.reduce((sum, [, count]) => sum + Number(count || 0), 0);
  if (!totalStudents) return null;
  const totalScore = entries.reduce(
    (sum, [level, count]) => sum + (Number(level || 0) * Number(count || 0)),
    0,
  );
  return totalScore / totalStudents;
};

const studentCountFromDistribution = (distribution) => {
  return Object.values(distribution || {}).reduce((sum, count) => sum + Number(count || 0), 0);
};

const truncateText = (value, maxLength = 110) => {
  const raw = String(value || '').trim();
  if (!raw) return '-';
  if (raw.length <= maxLength) return raw;
  return `${raw.slice(0, maxLength - 1).trim()}…`;
};

const formatShortPct = (value) => {
  if (value === null || typeof value === 'undefined' || Number.isNaN(Number(value))) return 'hazır değil';
  return `${Number(value).toFixed(Number(value) % 1 ? 1 : 0)}%`;
};

const sumBy = (rows, key) => {
  return (rows || []).reduce((sum, row) => sum + Number(row?.[key] || 0), 0);
};

const averageBy = (rows, key) => {
  const values = (rows || [])
    .map((row) => row?.[key])
    .filter((value) => value !== null && typeof value !== 'undefined' && !Number.isNaN(Number(value)))
    .map((value) => Number(value));
  if (!values.length) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const trendLabel = (current, previous, unit) => {
  const currentValue = Number(current || 0);
  const previousValue = Number(previous || 0);
  if (previousValue === 0 && currentValue === 0) return 'Hareket yok';
  if (previousValue === 0 && currentValue > 0) return `Yeni hareket: ${currentValue} ${unit}`;
  const diff = currentValue - previousValue;
  const prefix = diff > 0 ? '+' : '';
  return `${prefix}${diff} ${unit} önceki döneme göre`;
};

const progressValue = (value) => {
  const normalized = Number(value || 0);
  return Math.max(0, Math.min(100, normalized));
};

const flattenSkillRows = (summaryPayload) => {
  const lessons = summaryPayload?.lessons || [];
  return lessons.flatMap((lessonItem) => {
    const skills = lessonItem?.skills || {};
    return Object.entries(skills).map(([skillKey, skillData]) => ({
      lesson: lessonItem.lesson,
      skillKey,
      successPct: skillData?.success_pct,
      avgLevel: averageLevelFromDistribution(skillData?.level_distribution),
      studentCount: studentCountFromDistribution(skillData?.level_distribution),
      mostMissedQuestion: skillData?.most_missed_question || null,
    }));
  });
};

export default function MiniTaskStatisticsPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [schoolId, setSchoolId] = useState(null);
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [periodDays, setPeriodDays] = useState(28);
  const [summary, setSummary] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [bootLoading, setBootLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [studentLevelRows, setStudentLevelRows] = useState([]);
  const [studentLevelsLoading, setStudentLevelsLoading] = useState(false);
  const [studentLevelsError, setStudentLevelsError] = useState('');
  const [studentLevelsExpanded, setStudentLevelsExpanded] = useState(false);
  const [studentLevelsLoadedKey, setStudentLevelsLoadedKey] = useState('');
  const studentLevelsRequestIdRef = useRef(0);

  const selectedClass = useMemo(
    () => classes.find((item) => String(item.id) === String(selectedClassId)) || null,
    [classes, selectedClassId],
  );
  const studentLevelsKey = schoolId && selectedClassId ? `${schoolId}:${selectedClassId}` : '';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setError('');
        const me = await getMeCached();
        const sid = me?.data?.admin_user?.school_id;
        if (!sid) throw new Error('Okul bilgisi bulunamadı.');
        const classRows = await fetchClasses(sid);
        if (cancelled) return;
        const list = Array.isArray(classRows)
          ? [...classRows].sort((a, b) => String(a?.name || a?.code || '').localeCompare(String(b?.name || b?.code || ''), 'tr-TR'))
          : [];
        setSchoolId(sid);
        setClasses(list);
        setSelectedClassId(list[0]?.id ? String(list[0].id) : '');
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Mini Görev istatistikleri yüklenemedi.');
      } finally {
        if (!cancelled) setBootLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!schoolId || !selectedClassId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const [summaryPayload, statisticsPayload] = await Promise.all([
          fetchMiniTaskClassSummary(schoolId, selectedClassId),
          fetchMiniTaskClassStatistics(schoolId, selectedClassId, { days: periodDays }),
        ]);
        if (cancelled) return;
        setSummary(summaryPayload);
        setStatistics(statisticsPayload);
      } catch (err) {
        if (!cancelled) {
          setSummary(null);
          setStatistics(null);
          setError(err?.message || 'Mini Görev istatistikleri yüklenemedi.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [schoolId, selectedClassId, periodDays]);

  useEffect(() => {
    studentLevelsRequestIdRef.current += 1;
    setStudentLevelRows([]);
    setStudentLevelsLoading(false);
    setStudentLevelsError('');
    setStudentLevelsExpanded(false);
    setStudentLevelsLoadedKey('');
  }, [studentLevelsKey]);

  const loadStudentLevels = useCallback(async () => {
    if (!schoolId || !selectedClassId || !studentLevelsKey) return;
    if (studentLevelsLoading || studentLevelsLoadedKey === studentLevelsKey) return;

    const requestId = studentLevelsRequestIdRef.current + 1;
    studentLevelsRequestIdRef.current = requestId;

    try {
      setStudentLevelsLoading(true);
      setStudentLevelsError('');
      const payload = await fetchMiniTaskClassStudents(schoolId, selectedClassId);
      if (studentLevelsRequestIdRef.current !== requestId) return;
      setStudentLevelRows(payload?.students || []);
      setStudentLevelsLoadedKey(studentLevelsKey);
    } catch (err) {
      if (studentLevelsRequestIdRef.current !== requestId) return;
      setStudentLevelRows([]);
      setStudentLevelsError(err?.message || 'Öğrenci level görünümü yüklenemedi.');
    } finally {
      if (studentLevelsRequestIdRef.current === requestId) {
        setStudentLevelsLoading(false);
      }
    }
  }, [schoolId, selectedClassId, studentLevelsKey, studentLevelsLoadedKey, studentLevelsLoading]);

  const skillRows = useMemo(() => flattenSkillRows(summary), [summary]);

  const strongestSkill = useMemo(() => {
    const candidates = skillRows.filter((item) => item.successPct !== null && typeof item.successPct !== 'undefined');
    return [...candidates].sort((a, b) => Number(b.successPct) - Number(a.successPct))[0] || null;
  }, [skillRows]);

  const weakestSkill = useMemo(() => {
    const candidates = skillRows.filter((item) => item.successPct !== null && typeof item.successPct !== 'undefined');
    return [...candidates].sort((a, b) => Number(a.successPct) - Number(b.successPct))[0] || null;
  }, [skillRows]);

  const dailyUsage = useMemo(() => statistics?.daily_usage || [], [statistics?.daily_usage]);
  const lessonActivity = useMemo(() => statistics?.lesson_activity || [], [statistics?.lesson_activity]);
  const taskTypeMix = useMemo(() => statistics?.task_type_mix || [], [statistics?.task_type_mix]);
  const topStudents = useMemo(() => statistics?.top_students || [], [statistics?.top_students]);
  const attentionStudents = useMemo(() => statistics?.attention_students || [], [statistics?.attention_students]);
  const overview = statistics?.overview || {};

  const completedRoundsInWindow = Number(overview.completed_rounds_in_window || 0);
  const hasMeaningfulUsage = completedRoundsInWindow > 0;
  const studentLevelsReady = studentLevelsLoadedKey === studentLevelsKey;
  const studentsWithLevelsCount = useMemo(
    () => studentLevelRows.filter((item) => (item?.lessons || []).length > 0).length,
    [studentLevelRows],
  );
  const studentLevelStatusLabel = studentLevelsLoading
    ? 'Yükleniyor...'
    : studentLevelsReady
      ? `${studentsWithLevelsCount}/${studentLevelRows.length || 0} öğrencide level kaydı var`
      : 'Açınca yüklenir';

  const prioritizedSkills = useMemo(() => {
    return [...skillRows]
      .filter((item) => item.successPct !== null && typeof item.successPct !== 'undefined')
      .sort((a, b) => {
        if (Number(a.successPct) !== Number(b.successPct)) return Number(a.successPct) - Number(b.successPct);
        return Number(a.avgLevel || 0) - Number(b.avgLevel || 0);
      })
      .slice(0, 4);
  }, [skillRows]);

  const roundSum = (items) => items.reduce((sum, item) => sum + Number(item?.total_rounds || 0), 0);
  const currentWeekRounds = roundSum(dailyUsage.slice(-7));
  const previousWeekRounds = roundSum(dailyUsage.slice(-14, -7));
  const weeklyTrendPct = previousWeekRounds > 0
    ? Math.round(((currentWeekRounds - previousWeekRounds) / previousWeekRounds) * 100)
    : (currentWeekRounds > 0 ? 100 : 0);
  const weeklyTrendDirection = currentWeekRounds > previousWeekRounds
    ? 'up'
    : currentWeekRounds < previousWeekRounds
      ? 'down'
      : 'flat';

  const strategicSummary = useMemo(() => {
    if (!hasMeaningfulUsage) {
      return {
        title: 'Henüz anlamlı kullanım oluşmadı',
        detail: `Son ${periodDays} günde bu sınıfta tamamlanan mini görev görünmüyor. Bu durumda grafiklerden çok, ilk oturumu başlatmak ve akıllı tahta akışını doğrulamak daha değerli.`,
      };
    }
    if (Number(overview.participation_pct || 0) < 50) {
      return {
        title: 'Öncelik katılımı genişletmek olmalı',
        detail: `Sınıfın yalnızca ${formatPct(overview.participation_pct || 0)} kadarı aktif görünüyor. Önce kullanım düzenini oturtup ardından başarı ve kategori farklarını yorumlamak daha sağlıklı olur.`,
      };
    }
    if (weakestSkill) {
      return {
        title: `${lessonLabel(weakestSkill.lesson)} / ${skillLabel(weakestSkill.skillKey)} izleme alınmalı`,
        detail: `Bu alan sınıfın en kırılgan kategorisi. Sonraki oturumda aynı kategoriyi kısa, kontrollü bir tekrar olarak öne almak öğretim açısından daha verimli olur.`,
      };
    }
    return {
      title: 'Sınıf ritmi dengeli görünüyor',
      detail: 'Kullanım ve başarı verileri temel bir ritim gösteriyor. Bu aşamada amaç iyi giden alanları korurken zayıf öğrencilere mikro destek vermek olmalı.',
    };
  }, [hasMeaningfulUsage, overview.participation_pct, periodDays, weakestSkill]);

  const recommendedActions = useMemo(() => {
    const actions = [];
    if (!hasMeaningfulUsage) {
      actions.push({
        title: 'Tahta akışını bir kez canlı kontrol edin',
        detail: 'Kısayolun masaüstünden açıldığını ve öğrencilerin sınıf ekranına ulaştığını doğrulayın.',
        tone: 'cool',
      });
      actions.push({
        title: 'Sınıfla kısa bir başlangıç oturumu yapın',
        detail: 'Önce herkesin en az bir tur çözmesini hedefleyin. İlk veri oluşmadan karşılaştırmalı yorumlar zayıf kalır.',
        tone: 'warm',
      });
      actions.push({
        title: 'Bir hafta sonra tekrar bu ekrana dönün',
        detail: 'Düzenli kullanım başladıktan sonra hangi kategori ve öğrencilerin ayrıştığı daha net görünür.',
        tone: 'success',
      });
      return actions;
    }

    if (Number(overview.participation_pct || 0) < 70) {
      actions.push({
        title: 'Katılımı önce genişletin',
        detail: `${overview.active_student_count || 0}/${overview.roster_count || 0} öğrenci aktif. Birkaç öğrencide ritim oturmadan başarı ortalamaları yanıltıcı kalabilir.`,
        tone: 'cool',
      });
    }

    if (weakestSkill) {
      actions.push({
        title: `${skillLabel(weakestSkill.skillKey)} için kısa tekrar planlayın`,
        detail: `${lessonLabel(weakestSkill.lesson)} dersinde başarı ${formatPct(weakestSkill.successPct)} seviyesinde. Sonraki oturumda bu kategoriyi ilk sıraya almak mantıklı olur.`,
        tone: 'warm',
      });
    }

    if (attentionStudents.length) {
      actions.push({
        title: 'İlgi bekleyen öğrencileri öne alın',
        detail: `${attentionStudents.slice(0, 3).map((item) => item.student.display_name).join(', ')} için kısa bir hatırlatma veya yönlendirme fark yaratabilir.`,
        tone: 'default',
      });
    }

    if (strongestSkill) {
      actions.push({
        title: 'Güçlü alanı güven kazanımı için kullanın',
        detail: `${lessonLabel(strongestSkill.lesson)} / ${skillLabel(strongestSkill.skillKey)} alanı iyi gidiyor. Zor kategoriye geçmeden önce ısınma etkinliği olarak kullanılabilir.`,
        tone: 'success',
      });
    }

    return actions.slice(0, 3);
  }, [attentionStudents, hasMeaningfulUsage, overview.active_student_count, overview.participation_pct, overview.roster_count, strongestSkill, weakestSkill]);

  const recentWindow = useMemo(() => dailyUsage.slice(-7), [dailyUsage]);
  const previousWindow = useMemo(() => dailyUsage.slice(-14, -7), [dailyUsage]);
  const recentRounds = sumBy(recentWindow, 'total_rounds');
  const previousRounds = sumBy(previousWindow, 'total_rounds');
  const recentActiveAvg = averageBy(recentWindow, 'active_student_count');
  const recentScoreAvg = averageBy(recentWindow, 'avg_score_pct');
  const previousScoreAvg = averageBy(previousWindow, 'avg_score_pct');

  const activityMixText = useMemo(() => {
    const dominant = [...taskTypeMix].sort((a, b) => Number(b.round_count || 0) - Number(a.round_count || 0))[0];
    if (!dominant || !Number(dominant.round_count || 0)) return 'Denge verisi henüz oluşmadı';
    return `Ağırlık ${TASK_TYPE_SHORT_LABELS[dominant.task_type] || dominant.task_type} tarafında.`;
  }, [taskTypeMix]);

  const coachSummary = useMemo(() => {
    if (!hasMeaningfulUsage) {
      return {
        positives: [],
        warnings: [],
        nextSteps: [
          'Önce bu sınıf için akıllı tahta kısayolunu kurun.',
          'İlk uygulama oturumundan sonra bu ekran otomatik olarak günlük kullanım ve kategori bazlı başarıyı gösterecek.',
        ],
      };
    }

    const positives = [];
    const warnings = [];
    const nextSteps = [];

    if (strongestSkill) {
      positives.push(`${lessonLabel(strongestSkill.lesson)} / ${skillLabel(strongestSkill.skillKey)} şu an sınıfın en güçlü alanı (${formatShortPct(strongestSkill.successPct)}).`);
    }
    if (recentRounds > previousRounds) {
      positives.push(`Son 7 günde kullanım arttı: ${recentRounds} tur çözüldü.`);
    }
    if (recentScoreAvg !== null && previousScoreAvg !== null && recentScoreAvg > previousScoreAvg) {
      positives.push(`Başarı ortalaması yükseliyor: son 7 gün ${formatShortPct(recentScoreAvg)}.`);
    }

    if (weakestSkill) {
      warnings.push(`${lessonLabel(weakestSkill.lesson)} / ${skillLabel(weakestSkill.skillKey)} tekrar istemeye en yakın alan (${formatShortPct(weakestSkill.successPct)}).`);
    }
    if (attentionStudents.length) {
      warnings.push(`${attentionStudents.length} öğrenci seçilen dönemde görünmüyor; kısa bir hatırlatma veya sınıf içi yönlendirme yararlı olur.`);
    }
    if (previousRounds > 0 && recentRounds < previousRounds) {
      warnings.push(`Kullanım temposu düştü: son 7 gün ${recentRounds}, önceki 7 gün ${previousRounds} tur.`);
    }

    if (weakestSkill) {
      nextSteps.push(`Bir sonraki oturumda ${skillLabel(weakestSkill.skillKey)} kategorisini yeniden açın.`);
    }
    if (attentionStudents.length) {
      nextSteps.push('Önce ilgi bekleyen öğrencileri tahtaya davet ederek katılımı dengeli başlatın.');
    }
    if (!nextSteps.length) {
      nextSteps.push('Mevcut ritim sağlıklı görünüyor; güçlü kategorilerle motivasyonu koruyup zayıf alanları hafif tekrarlarla destekleyin.');
    }

    return { positives, warnings, nextSteps };
  }, [
    attentionStudents.length,
    hasMeaningfulUsage,
    previousRounds,
    previousScoreAvg,
    recentRounds,
    recentScoreAvg,
    strongestSkill,
    weakestSkill,
  ]);

  if (bootLoading) {
    return (
      <Box sx={{ minHeight: '70vh', display: 'grid', placeItems: 'center' }}>
        <Stack spacing={1.2} alignItems="center">
          <CircularProgress />
          <Typography color="text.secondary">Mini Görev istatistikleri yükleniyor...</Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box data-testid="mini-task-statistics-typography" sx={{
      p: { xs: 1.5, md: 3 }, maxWidth: 1280, mx: 'auto',
      width: '100%', minWidth: 0, fontSynthesis: 'none',
      ...chartTypographySx,
    }}>
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: '#d7e8ff',
          borderRadius: 4,
          px: { xs: 1.5, md: 3 },
          py: { xs: 1.5, md: 2.5 },
          mb: 2,
          background: 'linear-gradient(135deg, #fffdf7 0%, #f7fbff 55%, #eef6ff 100%)',
          overflow: 'hidden',
        }}
      >
        <Stack spacing={{ xs: 1.5, md: 2.2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 1, md: 1.5 }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            <Stack spacing={{ xs: 0.75, md: 1 }} sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                <Button
                  size="small"
                  startIcon={<ArrowBackRoundedIcon />}
                  onClick={() => navigate('/admin')}
                  sx={{ borderRadius: 999, px: 1.25, minWidth: 0 }}
                >
                  {isMobile ? 'Ana sayfa' : 'Ana sayfaya dön'}
                </Button>
                <Chip icon={<InsightsRoundedIcon />} label="Mini Görev İstatistikleri" sx={{ fontWeight: 600 }} />
                <Chip label={`Son görünüm: ${periodDays} gün`} variant="outlined" sx={{ fontWeight: 700, bgcolor: 'rgba(255,255,255,0.62)' }} />
              </Stack>
              <Typography variant="h4" sx={{ ...teacherTypography.pageTitle, maxWidth: { xs: '100%', md: 760 }, overflowWrap: 'anywhere' }}>
                {selectedClass?.name || selectedClass?.code || 'Sınıf'} için öğrenme ritmi
              </Typography>
              <Typography sx={{ ...descriptionSx, maxWidth: 760, fontSize: { xs: '0.95rem', sm: '1rem' } }}>
                {isMobile
                  ? 'Bu ekran üç kararı hızlandırır: zorlanan konu, kullanım temposu ve daha fazla teşvik gereken öğrenciler.'
                  : 'Bu ekran üç kararı hızlandırır: hangi ders veya kategori zorlanıyor, günlük kullanım düşüyor mu, hangi öğrenciler daha fazla teşvik gerektiriyor.'}
              </Typography>
            </Stack>
            <Button
              variant="outlined"
              startIcon={<FlashOnRoundedIcon />}
              onClick={() => navigate('/admin/mini-tasks')}
              sx={{ borderRadius: 999, fontWeight: 600, display: { xs: 'none', md: 'inline-flex' } }}
            >
              Kısayol üretimine dön
            </Button>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
            <TextField
              select
              label="Sınıf"
              size="small"
              value={selectedClassId}
              onChange={(event) => setSelectedClassId(event.target.value)}
              sx={{ width: { xs: '100%', md: 'auto' }, minWidth: { xs: 0, md: 280 } }}
            >
              {classes.map((item) => (
                <MenuItem key={item.id} value={String(item.id)}>
                  {item.name || item.code || item.id}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Zaman aralığı"
              size="small"
              value={String(periodDays)}
              onChange={(event) => setPeriodDays(Number(event.target.value))}
              sx={{ width: { xs: '100%', md: 'auto' }, minWidth: { xs: 0, md: 180 } }}
            >
              {PERIOD_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={String(option.value)}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <Chip
              icon={<Groups2RoundedIcon />}
              label={`${overview.roster_count || 0} öğrenci`}
              variant="outlined"
              sx={{ alignSelf: { xs: 'flex-start', md: 'center' }, fontWeight: 700, bgcolor: 'rgba(255,255,255,0.62)' }}
            />
          </Stack>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert>}

      {loading ? (
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, p: 4 }}>
          <Stack spacing={1.2} alignItems="center">
            <CircularProgress />
            <Typography color="text.secondary">İstatistikler güncelleniyor...</Typography>
          </Stack>
        </Paper>
      ) : (
        <Stack spacing={2.5}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' },
              gap: { xs: 1, sm: 1.5 },
            }}
          >
            <MetricCard
              icon={<SchoolRoundedIcon fontSize="small" />}
              title="Katılım"
              value={formatPct(overview.participation_pct || 0)}
              subtitle={`${overview.active_student_count || 0}/${overview.roster_count || 0} öğrenci bu dönemde aktif`}
              tone="cool"
            />
            <MetricCard
              icon={<TrendingUpRoundedIcon fontSize="small" />}
              title="Ortalama Başarı"
              value={formatPct(overview.avg_score_pct)}
              subtitle={`${overview.completed_rounds_in_window || 0} tamamlanan turdan hesaplandı`}
              tone="success"
            />
            <MetricCard
              icon={<FlashOnRoundedIcon fontSize="small" />}
              title="Haftalık Tempo"
              value={String(overview.week_round_count || 0)}
                subtitle={`Bugün ${overview.today_student_count || 0}, bu hafta ${overview.week_student_count || 0} öğrenci aktif`}
              tone="warm"
            />
            <MetricCard
              icon={<InsightsRoundedIcon fontSize="small" />}
              title="Seviye Ortalaması"
              value={formatLevel(overview.average_current_level)}
              subtitle="Güncel öğrenci seviye kayıtlarının ortalaması"
            />
          </Box>

          <Paper
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: '#d8e7f8',
              borderRadius: 4,
              p: { xs: 2, md: 2.5 },
              background: 'linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.15fr) minmax(0, 0.85fr)' },
                gap: 2,
              }}
            >
              <Stack spacing={1.4}>
                <Chip icon={<AutoGraphRoundedIcon />} label="Sınıf Özeti" sx={{ width: 'fit-content', fontWeight: 600 }} />
                <Typography variant="h5" sx={teacherTypography.sectionTitle}>
                  {strategicSummary.title}
                </Typography>
                <Typography sx={{ ...descriptionSx, maxWidth: 720 }}>
                  {strategicSummary.detail}
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Chip
                    label={`Son 7 gün: ${currentWeekRounds} tur`}
                    sx={{ fontWeight: 700, bgcolor: '#eff6ff', color: '#1d4ed8' }}
                  />
                  <Chip
                    icon={<NorthEastRoundedIcon sx={{ transform: weeklyTrendDirection === 'down' ? 'rotate(90deg)' : weeklyTrendDirection === 'flat' ? 'rotate(45deg)' : 'none' }} />}
                    label={previousWeekRounds > 0
                      ? `Önceki haftaya göre ${weeklyTrendPct > 0 ? '+' : ''}${weeklyTrendPct}%`
                      : (currentWeekRounds > 0 ? 'İlk aktif hafta' : 'Henüz haftalık trend yok')}
                    sx={{
                      fontWeight: 700,
                      bgcolor: weeklyTrendDirection === 'down' ? '#fff7ed' : '#f5fff7',
                      color: weeklyTrendDirection === 'down' ? '#c2410c' : '#15803d',
                    }}
                  />
                  <Chip
                    icon={<Groups2RoundedIcon />}
                    label={`${attentionStudents.length} öğrenci takipte`}
                    sx={{ fontWeight: 700 }}
                  />
                </Stack>
              </Stack>

              <Paper
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: '#e5edf7',
                  borderRadius: 3,
                  p: 1.75,
                  bgcolor: '#ffffff',
                }}
              >
                <Stack spacing={1.1}>
                  <Typography sx={teacherTypography.cardTitle}>Önerilen sonraki adımlar</Typography>
                  {recommendedActions.map((item, index) => (
                    <Box key={`${index}-${item.title}`}>
                      {index > 0 && <Divider sx={{ my: 0.5 }} />}
                      <Stack direction="row" spacing={1.1} alignItems="flex-start">
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            display: 'grid',
                            placeItems: 'center',
                            bgcolor: item.tone === 'warm' ? '#fff1df' : item.tone === 'success' ? '#eaf9ef' : '#eef5ff',
                            color: item.tone === 'warm' ? '#c2410c' : item.tone === 'success' ? '#15803d' : '#2563eb',
                            fontWeight: 700,
                            fontSize: 13,
                            flex: '0 0 auto',
                            mt: 0.2,
                          }}
                        >
                          {index + 1}
                        </Box>
                        <Box>
                          <Typography sx={{ fontWeight: 600, lineHeight: 1.25 }}>{item.title}</Typography>
                          <Typography variant="body2" sx={{ ...descriptionSx, mt: 0.35 }}>
                            {item.detail}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            </Box>
          </Paper>

          {!hasMeaningfulUsage ? (
            <SectionCard
              title="İlk oturum planı"
              subtitle="Boş grafikler yerine önce veri oluşmasını sağlayacak üç kısa adımı gösteriyoruz."
              action={<Chip icon={<TaskAltRoundedIcon />} label="Başlangıç akışı" sx={{ fontWeight: 600 }} />}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                  gap: 1.25,
                }}
              >
                <ActionCard index={1} title="Kısayolun tahtada açıldığını kontrol edin" tone="cool">
                  Kısayol üretim ekranından linkin doğru açıldığını ve sınıf listesinin göründüğünü doğrulayın.
                </ActionCard>
                <ActionCard index={2} title="Tüm sınıfla kısa bir ilk oturum yapın" tone="warm">
                  Amaç ilk derste başarı analizi değil, mümkün olduğunca çok öğrenciden en az birer tur verisi toplamak.
                </ActionCard>
                <ActionCard index={3} title="Bir hafta sonra ritmi yeniden değerlendirin" tone="success">
                  Düzenli kullanım başladıktan sonra bu ekran daha net bir öğretim karar paneline dönüşür.
                </ActionCard>
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                <Button
                  variant="contained"
                  startIcon={<FlashOnRoundedIcon />}
                  onClick={() => navigate('/admin/mini-tasks')}
                  sx={{ borderRadius: 999, fontWeight: 600 }}
                >
                  Kısayol ve tahta akışını aç
                </Button>
                <Chip label={`Son ${periodDays} günde tamamlanan tur: ${completedRoundsInWindow}`} sx={{ fontWeight: 700, width: 'fit-content' }} />
              </Stack>
            </SectionCard>
          ) : (
            <>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.6fr) minmax(0, 1fr)' },
                  gap: 2,
                }}
              >
                <SectionCard
                  title="Günlük kullanım eğrisi"
                  subtitle="Kullanım ritmini görmek için günlük çözüm sayısını ve aktif öğrenci sayısını birlikte izleyin."
                  action={<Chip label={trendLabel(recentRounds, previousRounds, 'tur')} sx={{ fontWeight: 700 }} />}
                >
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Chip label={`Son 7 gün: ${recentRounds} tur`} sx={{ fontWeight: 700 }} />
                    <Chip label={`Önceki 7 gün: ${previousRounds} tur`} variant="outlined" sx={{ fontWeight: 700 }} />
                    <Chip label={`Aktif ort.: ${recentActiveAvg ? recentActiveAvg.toFixed(1) : '0'}`} variant="outlined" sx={{ fontWeight: 700 }} />
                  </Stack>
                  <LineChart
                    height={isMobile ? 240 : 280}
                    margin={isMobile ? { top: 16, right: 8, bottom: 28, left: 30 } : { top: 20, right: 20, bottom: 36, left: 42 }}
                    xAxis={[{
                      scaleType: 'point',
                      data: dailyUsage.map((item) => formatDateShort(item.date)),
                    }]}
                    series={[
                      {
                        id: 'rounds',
                        label: 'Tur sayısı',
                        data: dailyUsage.map((item) => Number(item.total_rounds || 0)),
                        color: '#2563eb',
                        curve: 'monotoneX',
                        area: true,
                      },
                      {
                        id: 'students',
                        label: 'Aktif öğrenci',
                        data: dailyUsage.map((item) => Number(item.active_student_count || 0)),
                        color: '#f97316',
                        curve: 'monotoneX',
                      },
                    ]}
                  />
                </SectionCard>

                <SectionCard
                  title="Bugün için öğretmen yorumu"
                  subtitle="Ham veriyi yorumlamak yerine doğrudan sınıf içi karar cümleleri sunar."
                  action={<Chip label={activityMixText} variant="outlined" sx={{ fontWeight: 700 }} />}
                >
                  <Stack spacing={1.1}>
                    <CoachNote
                      title="İyi giden"
                      tone="success"
                      items={coachSummary.positives}
                      emptyText="Güçlü alanlar ve yükselen eğilimler burada görünecek."
                    />
                    <CoachNote
                      title="Dikkat isteyen"
                      tone="warning"
                      items={coachSummary.warnings}
                      emptyText="Şu an öne çıkan kritik bir risk sinyali görünmüyor."
                    />
                    <CoachNote
                      title="Önerilen sonraki adım"
                      tone="default"
                      items={coachSummary.nextSteps}
                      emptyText="İzlenecek sonraki adım burada belirecek."
                    />
                  </Stack>
                </SectionCard>
              </Box>

              {prioritizedSkills.length ? (
                <SectionCard
                  title="Öncelikli tekrar listesi"
                  subtitle="Öğretmenin bir sonraki oturumda önce bakması gereken kategorileri başarı düzeyine göre sıraladık."
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' },
                      gap: 1.5,
                    }}
                  >
                    {prioritizedSkills.map((item, index) => (
                      <Paper
                        key={`${item.lesson?.code}-${item.skillKey}`}
                        elevation={0}
                        sx={{
                          border: '1px solid',
                          borderColor: '#fde68a',
                          bgcolor: '#fffdf6',
                          borderRadius: 3,
                          p: 1.5,
                        }}
                      >
                        <Stack spacing={1}>
                          <Chip label={`Öncelik ${index + 1}`} size="small" sx={{ width: 'fit-content', fontWeight: 600 }} />
                          <Typography sx={teacherTypography.cardTitle}>
                            {lessonLabel(item.lesson)} / {skillLabel(item.skillKey)}
                          </Typography>
                          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                            <Chip label={`Başarı ${formatPct(item.successPct)}`} size="small" sx={{ fontWeight: 700 }} />
                            <Chip label={`Seviye ${formatLevel(item.avgLevel)}`} size="small" variant="outlined" sx={{ fontWeight: 700 }} />
                          </Stack>
                          <LinearProgress
                            variant="determinate"
                            value={progressValue(item.successPct)}
                            sx={{ height: 8, borderRadius: 999, bgcolor: '#ffe8c2', '& .MuiLinearProgress-bar': { borderRadius: 999, bgcolor: '#f59e0b' } }}
                          />
                          <Typography variant="body2" sx={descriptionSx}>
                            {item.studentCount || 0} öğrenci görünür durumda. En çok zorlanılan soru: {truncateText(item.mostMissedQuestion?.prompt || 'Henüz yeterli soru verisi yok.')}
                          </Typography>
                        </Stack>
                      </Paper>
                    ))}
                  </Box>
                </SectionCard>
              ) : null}

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' },
                  gap: 2,
                }}
              >
                <SectionCard
                  title="Ders ortalamaları"
                  subtitle="Hangi derslerde sınıf daha rahat ilerliyor, hangilerinde ek tekrar gerekiyor."
                >
                  {lessonActivity.length ? (
                    <BarChart
                      height={isMobile ? 240 : 300}
                      margin={isMobile ? { top: 16, right: 8, bottom: 40, left: 34 } : { top: 20, right: 20, bottom: 44, left: 48 }}
                      xAxis={[{
                        scaleType: 'band',
                        data: lessonActivity.map((item) => lessonLabel(item.lesson)),
                      }]}
                      series={[{
                        label: 'Başarı %',
                        data: lessonActivity.map((item) => Number(item.avg_score_pct || 0)),
                        color: '#0f766e',
                      }]}
                    />
                  ) : (
                    <Alert severity="info">Henüz ders bazlı ortalama gösterecek veri yok.</Alert>
                  )}
                </SectionCard>

                <SectionCard
                  title="Görev dengesi"
                  subtitle="Ders görevi ile zihin oyunları arasındaki dağılımı görün."
                  action={<Chip label={activityMixText} variant="outlined" sx={{ fontWeight: 700 }} />}
                >
                  {taskTypeMix.some((item) => Number(item?.round_count || 0) > 0) ? (
                    <BarChart
                      height={isMobile ? 240 : 300}
                      margin={isMobile ? { top: 16, right: 8, bottom: 40, left: 34 } : { top: 20, right: 20, bottom: 44, left: 48 }}
                      xAxis={[{
                        scaleType: 'band',
                        data: taskTypeMix.map((item) => TASK_TYPE_LABELS[item.task_type] || humanizeKey(item.task_type)),
                      }]}
                      series={[{
                        label: 'Tur sayısı',
                        data: taskTypeMix.map((item) => Number(item.round_count || 0)),
                        color: '#7c3aed',
                      }]}
                    />
                  ) : (
                    <Alert severity="info">Seçilen aralıkta etkinlik dağılımı oluşmadı.</Alert>
                  )}
                </SectionCard>
              </Box>

              <SectionCard
                title="Konu ve kategori tablosu"
                subtitle="Ayrıntılı görünüm için tüm kategorileri tek tabloda tutuyoruz; ancak öğretim önceliği için önce yukarıdaki tekrar listesini kullanın."
              >
                {skillRows.length ? (
                  <>
                    <Box
                      sx={{
                        display: { xs: 'grid', md: 'none' },
                        gap: 1.2,
                      }}
                    >
                      {[...skillRows]
                        .sort((a, b) => {
                          const aScore = a.successPct === null || typeof a.successPct === 'undefined' ? Number.POSITIVE_INFINITY : Number(a.successPct);
                          const bScore = b.successPct === null || typeof b.successPct === 'undefined' ? Number.POSITIVE_INFINITY : Number(b.successPct);
                          if (aScore !== bScore) return aScore - bScore;
                          return skillLabel(a.skillKey).localeCompare(skillLabel(b.skillKey), 'tr-TR');
                        })
                        .map((row) => (
                          <Paper
                            key={`${row.lesson?.code}-${row.skillKey}`}
                            elevation={0}
                            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, p: 1.3, minWidth: 0 }}
                          >
                            <Stack spacing={1}>
                              <Typography sx={teacherTypography.cardTitle}>
                                {lessonLabel(row.lesson)} / {skillLabel(row.skillKey)}
                              </Typography>
                              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                                <Chip size="small" label={`Başarı ${formatPct(row.successPct)}`} sx={{ fontWeight: 700 }} />
                                <Chip size="small" label={`Seviye ${formatLevel(row.avgLevel)}`} variant="outlined" sx={{ fontWeight: 700 }} />
                                <Chip size="small" label={`${row.studentCount || 0} öğrenci`} variant="outlined" sx={{ fontWeight: 700 }} />
                              </Stack>
                              <Typography variant="body2" sx={descriptionSx}>
                                En çok zorlanılan soru: {truncateText(row.mostMissedQuestion?.prompt || '-', 90)}
                              </Typography>
                            </Stack>
                          </Paper>
                        ))}
                    </Box>

                    <TableContainer sx={{ display: { xs: 'none', md: 'block' }, borderRadius: 3, border: '1px solid', borderColor: 'divider', overflowX: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={teacherTypography.tableHeader}>Ders</TableCell>
                            <TableCell sx={teacherTypography.tableHeader}>Kategori</TableCell>
                            <TableCell sx={teacherTypography.tableHeader}>Başarı</TableCell>
                            <TableCell sx={teacherTypography.tableHeader}>Ort. seviye</TableCell>
                            <TableCell sx={teacherTypography.tableHeader}>Öğrenci</TableCell>
                            <TableCell sx={teacherTypography.tableHeader}>En çok zorlanılan soru</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {[...skillRows]
                            .sort((a, b) => {
                              const aScore = a.successPct === null || typeof a.successPct === 'undefined' ? Number.POSITIVE_INFINITY : Number(a.successPct);
                              const bScore = b.successPct === null || typeof b.successPct === 'undefined' ? Number.POSITIVE_INFINITY : Number(b.successPct);
                              if (aScore !== bScore) return aScore - bScore;
                              return skillLabel(a.skillKey).localeCompare(skillLabel(b.skillKey), 'tr-TR');
                            })
                            .map((row) => (
                              <TableRow key={`${row.lesson?.code}-${row.skillKey}`} hover>
                                <TableCell>{lessonLabel(row.lesson)}</TableCell>
                                <TableCell>{skillLabel(row.skillKey)}</TableCell>
                                <TableCell>{formatPct(row.successPct)}</TableCell>
                                <TableCell>{formatLevel(row.avgLevel)}</TableCell>
                                <TableCell>{row.studentCount || 0}</TableCell>
                                <TableCell sx={{ maxWidth: 420 }}>
                                  {truncateText(row.mostMissedQuestion?.prompt || '-', 140)}
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                ) : (
                  <Alert severity="info">Henüz kategori bazlı başarı verisi oluşmadı.</Alert>
                )}
              </SectionCard>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' },
                  gap: 2,
                }}
              >
                <StudentSignalCard
                  title="En aktif öğrenciler"
                  subtitle="Sınıf içinde ivme yaratan öğrencileri görün ve olumlu örnek olarak kullanın."
                  items={topStudents}
                  emptyTitle="Seçilen aralıkta öğrenci aktivitesi oluşmadı."
                />

                <StudentSignalCard
                  title="İlgi bekleyen öğrenciler"
                  subtitle="Son dönemde görünmeyen öğrencileri erken fark edin; küçük bir hatırlatma bile yeterli olabilir."
                  items={attentionStudents}
                  emptyTitle="Seçilen dönemde tüm öğrenciler en az bir kez aktif görünüyor."
                  emptySeverity="success"
                  tone="warning"
                />
              </Box>

              <Accordion
                disableGutters
                elevation={0}
                expanded={studentLevelsExpanded}
                onChange={(_event, expanded) => {
                  setStudentLevelsExpanded(expanded);
                  if (expanded) loadStudentLevels();
                }}
                TransitionProps={{ unmountOnExit: true }}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '16px !important',
                  overflow: 'hidden',
                  bgcolor: '#ffffff',
                  '&::before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ px: { xs: 1.75, sm: 2.25 }, py: 0.5 }}
                >
                  <Stack spacing={1} sx={{ minWidth: 0, width: '100%' }}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      justifyContent="space-between"
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" sx={teacherTypography.sectionTitle}>
                          Öğrenci bazlı level görünümü
                        </Typography>
                        <Typography sx={{ ...descriptionSx, mt: 0.35, fontSize: { xs: '0.92rem', sm: '0.98rem' } }}>
                          Güncel level snapshot&apos;ını öğrenci bazında açın. Günlük karar için şart değil; detaylı sınıf planlaması için yararlı.
                        </Typography>
                      </Box>
                      <Chip
                        label={studentLevelStatusLabel}
                        variant="outlined"
                        sx={{ fontWeight: 700, alignSelf: { xs: 'flex-start', sm: 'center' } }}
                      />
                    </Stack>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: { xs: 1.5, sm: 2.25 }, pb: 2.25, pt: 0.5 }}>
                  {studentLevelsLoading ? (
                    <Stack spacing={1.2} alignItems="center" sx={{ py: 2 }}>
                      <CircularProgress size={28} />
                      <Typography color="text.secondary">Öğrenci level görünümü yükleniyor...</Typography>
                    </Stack>
                  ) : studentLevelsError ? (
                    <Alert severity="error">{studentLevelsError}</Alert>
                  ) : studentLevelsReady ? (
                    studentLevelRows.length ? (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' },
                        gap: 1.5,
                      }}
                    >
                      {studentLevelRows.map((item) => (
                        <Paper
                          key={item.student.id}
                          elevation={0}
                          sx={{
                            border: '1px solid',
                            borderColor: '#dbe7f5',
                            borderRadius: 3,
                            p: 1.5,
                            bgcolor: '#fcfdff',
                          }}
                        >
                          <Stack spacing={1.2}>
                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              spacing={1}
                              justifyContent="space-between"
                              alignItems={{ xs: 'flex-start', sm: 'center' }}
                            >
                              <Box>
                                <Typography sx={teacherTypography.studentName}>{item.student.display_name}</Typography>
                                <Typography variant="body2" sx={descriptionSx}>
                                  {item.last_round_at ? `Son tur: ${formatDateTime(item.last_round_at)}` : 'Henüz tamamlanmış tur yok'}
                                </Typography>
                              </Box>
                              <Chip label={`${item.total_round_count || 0} toplam tur`} size="small" sx={{ fontWeight: 700 }} />
                            </Stack>

                            {(item.lessons || []).length ? (
                              <Stack spacing={1}>
                                {(item.lessons || []).map((lessonItem) => (
                                  <Paper
                                    key={`${item.student.id}-${lessonItem.lesson.id}`}
                                    elevation={0}
                                    sx={{
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      borderRadius: 2.5,
                                      p: 1.15,
                                      bgcolor: '#ffffff',
                                    }}
                                  >
                                    <Stack spacing={0.9}>
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {lessonLabel(lessonItem.lesson)}
                                      </Typography>
                                      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                                        {Object.entries(lessonItem.levels || {}).map(([skillKey, level]) => (
                                          <Chip
                                            key={`${item.student.id}-${lessonItem.lesson.id}-${skillKey}`}
                                            size="small"
                                            label={`${skillLabel(skillKey)} · Seviye ${level}`}
                                            variant="outlined"
                                            sx={{ fontWeight: 700 }}
                                          />
                                        ))}
                                      </Stack>
                                    </Stack>
                                  </Paper>
                                ))}
                              </Stack>
                            ) : (
                              <Alert severity="info">Bu öğrencide henüz level kaydı oluşmadı.</Alert>
                            )}
                          </Stack>
                        </Paper>
                      ))}
                    </Box>
                    ) : (
                      <Alert severity="info">Seçilen sınıfta öğrenci listesi bulunamadı.</Alert>
                    )
                  ) : (
                    <Alert severity="info">Detayı açtığınızda öğrenci bazlı level görünümü yüklenecek.</Alert>
                  )}
                </AccordionDetails>
              </Accordion>

              {!isMobile ? (
                <SectionCard
                  title="Okuma rehberi"
                  subtitle="Sayfayı ilk kez açan biri için bu üç bölüme bakmak genellikle yeterlidir."
                  action={<Chip label="2 dakikada okunur" variant="outlined" sx={{ fontWeight: 700 }} />}
                >
                  <Stack spacing={1.2}>
                    {[
                      'Önce üstteki kartlardan katılım ve tempo değişimini kontrol edin.',
                      'Ardından öğretmen yorumu kartında tekrar gerektiren kategori ve önerilen adımı okuyun.',
                      'Son olarak ilgi bekleyen öğrenciler bölümünden kime öncelik vereceğinizi seçin.',
                    ].map((item, index) => (
                      <Stack key={item} direction="row" spacing={1.2} alignItems="flex-start">
                        <Chip label={String(index + 1)} sx={{ width: 28, height: 28, borderRadius: '50%', fontWeight: 700 }} />
                        <Typography sx={{ color: 'text.secondary' }}>{item}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </SectionCard>
              ) : null}
            </>
          )}
        </Stack>
      )}
    </Box>
  );
}
